"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.rbgToHsl = exports.parseRgbaString = exports.rgbaToString = exports.rgbaToHexa = exports.rgbToHex = exports.hexToLuma = exports.hexToRgba = exports.hexToRgb = void 0;
const RGB_HEX_REGEX = /^#([A-Fa-f0-9]{3}){1,2}/;
/**
 * Extract red, green, and blue values from an RGB hex string.
 * @param hex string containing RGB color value.
 * @returns an object with the rgb values.
 * @throws `Error` if `hex` is not well formed.
 *
 * @private exported for tests
 */
const hexToRgb = (hex) => {
    if (!RGB_HEX_REGEX.test(hex)) {
        throw new Error(`${hex} does not represent an RGB color.`);
    }
    let color = hex.substring(1).split('');
    if (color.length === 3) {
        color = [
            color[0] ?? '',
            color[0] ?? '',
            color[1] ?? '',
            color[1] ?? '',
            color[2] ?? '',
            color[2] ?? '',
        ];
    }
    const hexRep = parseInt(`0x${color.join('')}`, 16);
    return {
        r: (hexRep >> 16) & 0xff,
        g: (hexRep >> 8) & 0xff,
        b: hexRep & 0xff,
    };
};
exports.hexToRgb = hexToRgb;
/**
 * Create an RGBA color string from `hex` with the given `alpha` value.
 * @param hex string containing RGB color value.
 * @param alpha value to use for opacity.
 * @returns the destructured rgba value.
 * @throws `Error` if `hex` is not well formed.
 * @throws `Error` if `alpha` is not in the range [0, 1]
 * @see https://stackoverflow.com/a/5624139
 */
const hexToRgba = (hex, alpha = 1) => {
    if (alpha < 0 || alpha > 1) {
        throw new Error(`${alpha} is not a valid alpha value, in range [0, 1]`);
    }
    const rgb = (0, exports.hexToRgb)(hex);
    return { ...rgb, a: alpha };
};
exports.hexToRgba = hexToRgba;
/**
 * Compute relative luminance from an RGB value.
 * @param hex string containing RGB color value.
 * @returns relative luminance of the color represented by `hex`.
 * @see https://en.wikipedia.org/wiki/Relative_luminance
 * @see https://stackoverflow.com/a/596243
 */
const hexToLuma = (hex) => {
    const { r, g, b } = (0, exports.hexToRgb)(hex);
    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709
    return Math.round(luma);
};
exports.hexToLuma = hexToLuma;
/**
 * Converts an rgba object to a hex color string
 * @param r number red value
 * @param g number green value
 * @param b number blue value
 * @returns string hex value
 * @see https://stackoverflow.com/a/5624139
 */
function rgbToHex(r, g, b) {
    return ('#' +
        [r, g, b]
            .map((x) => {
            const hex = x.toString(16);
            return hex.length === 1 ? '0' + hex : hex;
        })
            .join(''));
}
exports.rgbToHex = rgbToHex;
/**
 * Converts an rgba string to a hex color string
 * @param rgba string rgba value
 * @param forceRemoveAlpha boolean if true, alpha will be removed
 * @returns string hex value
 * @see https://stackoverflow.com/questions/49974145/how-to-convert-rgba-to-hex-color-code-using-javascript
 * @example
 * rgbaToHexa({rgba: 'rgba(255, 255, 255, 0.5)'}) // '#ffffff80'
 */
function rgbaToHexa(params) {
    const { rgba, forceRemoveAlpha = false } = params;
    return ('#' +
        rgba
            .replace(/^rgba?\(|\s+|\)$/g, '') // Get's rgba / rgb string values
            .split(',') // splits them at ","
            .filter((string, index) => !forceRemoveAlpha || index !== 3)
            .map((string) => parseFloat(string)) // Converts them to numbers
            .map((number, index) => (index === 3 ? Math.round(number * 255) : number)) // Converts alpha to 255 number
            .map((number) => number.toString(16)) // Converts numbers to hex
            .map((string) => (string.length === 1 ? '0' + string : string)) // Adds 0 when length of one number is 1
            .join('')); // Puts the array to together to a string
}
exports.rgbaToHexa = rgbaToHexa;
/** Formats the `RGBA` object as an `rgba()` color string */
function rgbaToString(rgba) {
    return `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`;
}
exports.rgbaToString = rgbaToString;
/**
 * Parses an rgba string into an rgba object
 * @param rgbaString string rgba value
 * @returns object with r, g, b, a values
 */
function parseRgbaString(rgbaString) {
    if (!rgbaString) {
        return { r: 0, g: 0, b: 0, a: 1 };
    }
    // TODO: Add validation that values are valid
    const rgbaArray = rgbaString.replace('rgba(', '').replace(')', '').split(',');
    return {
        r: parseInt(rgbaArray[0] ?? '0', 10),
        g: parseInt(rgbaArray[1] ?? '0', 10),
        b: parseInt(rgbaArray[2] ?? '0', 10),
        a: parseFloat(rgbaArray[3] ?? '0'),
    };
}
exports.parseRgbaString = parseRgbaString;
/**
 * Converts an rgba object to an hsla object
 * @param r number red value
 * @param g number green value
 * @param b number blue value
 * @param a number alpha value
 * @returns object with h, s, l, a values
 * @see https://stackoverflow.com/a/54071699
 */
function rbgToHsl(r, g, b, a) {
    const v = Math.max(r, g, b), c = v - Math.min(r, g, b), f = 1 - Math.abs(v + v - c - 1);
    const h = c && (v == r ? (g - b) / c : v == g ? 2 + (b - r) / c : 4 + (r - g) / c);
    return {
        h: 60 * (h < 0 ? h + 6 : h),
        s: f ? c / f : 0,
        l: (v + v - c) / 2,
        a,
    };
}
exports.rbgToHsl = rbgToHsl;
